import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faClock, faCircleInfo, faEnvelope, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignedContracts } from '../hooks';
import type { SignedContract } from '../adapters/SignedContractsApiAdapter';

export interface SignedContractsModalRef {
  open: (email: string) => void;
}

interface SignedContractsModalProps {
  onSelect: (contract: SignedContract) => void;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

export const SignedContractsModal = forwardRef<SignedContractsModalRef, SignedContractsModalProps>(
  ({ onSelect }, ref) => {
    const { t } = useTranslation('createEditClientsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const { contracts, isLoading, error } = useSignedContracts(email || undefined, isOpen);

    useImperativeHandle(ref, () => ({
      open: (emailValue: string) => {
        setEmail(emailValue);
        setIsOpen(true);
        modalRef.current?.openModal();
      },
    }));

    const handleClose = () => {
      // Solo actualizamos el estado local cuando el modal se cierra
      // El propio ModalCenter se encarga de gestionar la animación/cierre visual
      setIsOpen(false);
    };

    const handleSelect = (contract: SignedContract) => {
      onSelect(contract);
      // Cerramos el modal de forma controlada y actualizamos el estado
      setIsOpen(false);
      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('signedContractsModal.title')}
        type="default"
        width="lg"
        onCloseModal={handleClose}
      >
        {!email && <div className="text-sm text-complementary-text py-4">{t('signedContractsModal.noEmail')}</div>}

        {email && (
          <div className="space-y-4">
            {isLoading && (
              <div className="text-sm text-complementary-text py-4">{t('signedContractsModal.loading')}</div>
            )}

            {!isLoading && error && <div className="text-sm text-error py-4">{t('signedContractsModal.error')}</div>}

            {!isLoading && !error && contracts.length === 0 && (
              <div className="text-sm text-complementary-text py-4">{t('signedContractsModal.empty')}</div>
            )}

            {!isLoading && !error && contracts.length > 0 && (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {contracts.map((contract) => (
                  <div
                    key={contract.envelope_id}
                    className="flex items-center justify-between p-3 border border-card-border rounded-md bg-card-bg"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-title">{contract.envelope_id}</span>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-complementary-text">
                        {contract.name && (
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5" />
                            <span>{contract.name}</span>
                          </span>
                        )}
                        {contract.email && (
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faEnvelope} className="w-3.5 h-3.5" />
                            <span>{contract.email}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faCircleInfo} className="w-3.5 h-3.5" />
                          <span>{contract.status}</span>
                        </span>
                        {contract['status-signer'] && contract['status-signer'] !== contract.status && (
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faCircleInfo} className="w-3.5 h-3.5" />
                            <span>{contract['status-signer']}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faClock} className="w-3.5 h-3.5" />
                          <span>{formatDate(contract.status_changed_date_time)}</span>
                        </span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => handleSelect(contract)}
                      data-testid="select-signed-contract-button"
                    >
                      {t('signedContractsModal.selectButton')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ModalCenter>
    );
  },
);

SignedContractsModal.displayName = 'SignedContractsModal';
